import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Bell, Mail, MessageCircle, MapPin, Loader2, Clock, Calendar } from 'lucide-react';
import { TelegramLinkFlow } from './TelegramLinkFlow';
import { LocalHolidaysManager } from './LocalHolidaysManager';

interface NotificationPreferences {
  deadline_notifications_app: boolean;
  deadline_notifications_email: boolean;
  deadline_notifications_telegram: boolean;
  telegram_chat_id: string | null;
  autonomous_community: string;
  email: string;
  default_reminder_days: number[];
  notification_hour: number;
}

const AUTONOMOUS_COMMUNITIES = [
  { value: 'andalucia', label: 'Andalucía' },
  { value: 'extremadura', label: 'Extremadura' },
  { value: 'otra', label: 'Otra / Nacional' },
];

const REMINDER_DAY_OPTIONS = [
  { value: 30, label: '30 días antes' },
  { value: 15, label: '15 días antes' },
  { value: 7, label: '7 días antes' },
  { value: 5, label: '5 días antes' },
  { value: 3, label: '3 días antes' },
  { value: 2, label: '2 días antes' },
  { value: 1, label: '1 día antes' },
  { value: 0, label: 'El mismo día' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`,
}));

export function NotificationSettingsTab() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingReminderDays, setPendingReminderDays] = useState<number[]>([]);
  const [hasReminderChanges, setHasReminderChanges] = useState(false);

  const loadPreferences = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('deadline_notifications_app, deadline_notifications_email, deadline_notifications_telegram, telegram_chat_id, autonomous_community, email, default_reminder_days, notification_hour')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      const prefs = {
        deadline_notifications_app: data.deadline_notifications_app ?? true,
        deadline_notifications_email: data.deadline_notifications_email ?? true,
        deadline_notifications_telegram: data.deadline_notifications_telegram ?? false,
        telegram_chat_id: data.telegram_chat_id,
        autonomous_community: data.autonomous_community || 'otra',
        email: data.email,
        default_reminder_days: (data as any).default_reminder_days ?? [7, 3, 1, 0],
        notification_hour: (data as any).notification_hour ?? 8,
      };

      setPreferences(prefs);
      setPendingReminderDays(prefs.default_reminder_days);
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Error al cargar las preferencias');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const updatePreference = async (key: keyof NotificationPreferences, value: boolean | string | number | number[]) => {
    if (!user || !preferences) return;

    setIsSaving(true);
    const previousValue = preferences[key];
    
    // Optimistic update
    setPreferences(prev => prev ? { ...prev, [key]: value } : null);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [key]: value })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Preferencia actualizada');
    } catch (error) {
      console.error('Error updating preference:', error);
      // Rollback on error
      setPreferences(prev => prev ? { ...prev, [key]: previousValue } : null);
      toast.error('Error al actualizar la preferencia');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleReminderDay = (day: number) => {
    const newDays = pendingReminderDays.includes(day)
      ? pendingReminderDays.filter(d => d !== day)
      : [...pendingReminderDays, day].sort((a, b) => b - a);
    
    setPendingReminderDays(newDays);
    setHasReminderChanges(true);
  };

  const saveReminderDays = async () => {
    if (pendingReminderDays.length === 0) {
      toast.error('Debes seleccionar al menos un día de recordatorio');
      return;
    }
    await updatePreference('default_reminder_days', pendingReminderDays);
    setHasReminderChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Error al cargar las preferencias
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium">Notificaciones de Plazos</h3>
        <p className="text-sm text-muted-foreground">
          Configura cómo y dónde quieres recibir recordatorios de tus plazos administrativos.
        </p>
      </div>

      {/* Notification Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Canales de notificación
          </CardTitle>
          <CardDescription>
            Elige por qué canales quieres recibir recordatorios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* In-App Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Bell className="h-4 w-4 text-primary" />
              </div>
              <div>
                <Label htmlFor="app-notifications" className="font-medium">
                  Notificaciones in-app
                </Label>
                <p className="text-sm text-muted-foreground">
                  Toasts y centro de notificaciones
                </p>
              </div>
            </div>
            <Switch
              id="app-notifications"
              checked={preferences.deadline_notifications_app}
              onCheckedChange={(checked) => updatePreference('deadline_notifications_app', checked)}
              disabled={isSaving}
            />
          </div>

          <Separator />

          {/* Email Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <Label htmlFor="email-notifications" className="font-medium">
                  Notificaciones por email
                </Label>
                <p className="text-sm text-muted-foreground">
                  Se enviarán a: {preferences.email}
                </p>
              </div>
            </div>
            <Switch
              id="email-notifications"
              checked={preferences.deadline_notifications_email}
              onCheckedChange={(checked) => updatePreference('deadline_notifications_email', checked)}
              disabled={isSaving}
            />
          </div>

          <Separator />

          {/* Telegram Notifications */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sky-100 dark:bg-sky-900/30 rounded-lg">
                  <MessageCircle className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="telegram-notifications" className="font-medium">
                      Notificaciones por Telegram
                    </Label>
                    {preferences.telegram_chat_id ? (
                      <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                        Vinculado
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        No vinculado
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Recibe alertas instantáneas vía @MunilexPlazosBot
                  </p>
                </div>
              </div>
              <Switch
                id="telegram-notifications"
                checked={preferences.deadline_notifications_telegram}
                onCheckedChange={(checked) => updatePreference('deadline_notifications_telegram', checked)}
                disabled={isSaving || !preferences.telegram_chat_id}
              />
            </div>

            {/* Telegram Link Flow */}
            <div className="ml-11">
              <TelegramLinkFlow
                telegramChatId={preferences.telegram_chat_id}
                onLinked={loadPreferences}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reminder Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Días de recordatorio
          </CardTitle>
          <CardDescription>
            Selecciona con cuántos días de antelación quieres recibir recordatorios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {REMINDER_DAY_OPTIONS.map((option) => (
              <div
                key={option.value}
                className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  pendingReminderDays.includes(option.value)
                    ? 'bg-primary/10 border-primary'
                    : 'bg-muted/30 border-transparent hover:border-muted-foreground/20'
                }`}
                onClick={() => toggleReminderDay(option.value)}
              >
                <Checkbox
                  id={`day-${option.value}`}
                  checked={pendingReminderDays.includes(option.value)}
                  onCheckedChange={() => toggleReminderDay(option.value)}
                />
                <Label
                  htmlFor={`day-${option.value}`}
                  className="text-sm cursor-pointer flex-1"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </div>

          {hasReminderChanges && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={saveReminderDays}
                disabled={isSaving || pendingReminderDays.length === 0}
                size="sm"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar cambios'
                )}
              </Button>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Estos días se aplicarán por defecto a los nuevos plazos. Puedes personalizarlos individualmente al crear cada plazo.
          </p>
        </CardContent>
      </Card>

      {/* Notification Time */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Hora de notificación
          </CardTitle>
          <CardDescription>
            A qué hora del día prefieres recibir los recordatorios (hora de España)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="notification-hour">Hora preferida</Label>
            <Select
              value={preferences.notification_hour.toString()}
              onValueChange={(value) => updatePreference('notification_hour', parseInt(value, 10))}
              disabled={isSaving}
            >
              <SelectTrigger id="notification-hour" className="w-full sm:w-[180px]">
                <SelectValue placeholder="Selecciona hora" />
              </SelectTrigger>
              <SelectContent>
                {HOUR_OPTIONS.map((hour) => (
                  <SelectItem key={hour.value} value={hour.value.toString()}>
                    {hour.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Los recordatorios por email y Telegram se enviarán aproximadamente a esta hora.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Regional Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Configuración regional
          </CardTitle>
          <CardDescription>
            Selecciona tu comunidad autónoma para el cálculo de días hábiles
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="autonomous-community">Comunidad Autónoma</Label>
            <Select
              value={preferences.autonomous_community}
              onValueChange={(value) => updatePreference('autonomous_community', value)}
              disabled={isSaving}
            >
              <SelectTrigger id="autonomous-community" className="w-full sm:w-[280px]">
                <SelectValue placeholder="Selecciona tu comunidad" />
              </SelectTrigger>
              <SelectContent>
                {AUTONOMOUS_COMMUNITIES.map((community) => (
                  <SelectItem key={community.value} value={community.value}>
                    {community.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Se usarán los festivos autonómicos correspondientes para calcular los días hábiles.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Local Holidays Manager */}
      <LocalHolidaysManager />
    </div>
  );
}
